'use client';

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Popover from '../Popover/Popover';
import Icon from '../Icon';
import { useProgress } from '@bprogress/next';
import s from './CompanySelect.module.scss';
import { t } from '../../i18n';

const SELECTED_HOLDING_PREFIX = 'selectedHolding:';

const CompanySelect = ({
    holdings = [],
    activeHoldingId,
    setActiveHolding,
    itemRefs,
    disabled,
    preventPageRefresh = false,
    activeTab,
    activeCountry,
}) => {
    const { start } = useProgress();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const popoverRef = useRef(null);

    const togglePopover = () => {
        setIsPopoverOpen(!isPopoverOpen);
    };

    const handleClickOutside = (event) => {
        if (popoverRef.current && !popoverRef.current.contains(event.target)) {
            setIsPopoverOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const persistHolding = (holdingId) => {
        if (typeof window === 'undefined') return;
        const code = activeCountry?.code;
        if (!code) return;
        const key = `${SELECTED_HOLDING_PREFIX}${code}`;
        try {
            if (holdingId) {
                sessionStorage.setItem(key, holdingId);
            } else {
                sessionStorage.removeItem(key);
            }
        } catch (e) {}
    };

    const handleHoldingSelect = (holding) => {
        start();
        setActiveHolding(holding.sourceId);
        persistHolding(holding.sourceId);

        const url = new URL(window.location.href);
        url.searchParams.set('holding', holding.sourceId);

        if (preventPageRefresh) {
            window.history.pushState({}, '', url.toString());
        } else {
            window.location.href = url.toString();
        }

        setIsPopoverOpen(false);
    };

    const handleClearAllFilters = () => {
        setActiveHolding(null);
        persistHolding(null);

        const url = new URL(window.location.href);
        url.searchParams.delete('holding');

        if (preventPageRefresh) {
            window.history.pushState({}, '', url.toString());
        } else {
            window.location.href = url.toString();
        }

        setIsPopoverOpen(false);
    };

    const selectedHolding = holdings.find(
        (h) => h.sourceId === activeHoldingId
    );

    return (
        <Popover
            minWidth="300px"
            onClick={togglePopover}
            isPopoverOpen={isPopoverOpen}
            popoverRef={popoverRef}
            disabled={disabled}
            modifier={'FullWidth'}
            content={
                <HoldingsListInPopover
                    holdings={holdings}
                    activeHoldingId={activeHoldingId}
                    onSelect={handleHoldingSelect}
                    itemRefs={itemRefs}
                    onClearAllFilters={handleClearAllFilters}
                    activeTab={activeTab}
                />
            }>
            <div
                className={classNames(s.CompanySelectorButton, {
                    [s.Open]: isPopoverOpen,
                    [s.Disabled]: disabled,
                })}>
                <span className={s.CompanySelectorText}>
                    {selectedHolding?.value ||
                        selectedHolding?.name ||
                        t('general.selectCompany')}
                </span>
                <Icon
                    name="ArrowRightBlack"
                    className={s.CompanySelectorArrowIcon}
                />
            </div>
        </Popover>
    );
};

CompanySelect.propTypes = {
    holdings: PropTypes.array.isRequired,
    activeHoldingId: PropTypes.string,
    setActiveHolding: PropTypes.func.isRequired,
    itemRefs: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    preventPageRefresh: PropTypes.bool,
    activeTab: PropTypes.string,
    activeCountry: PropTypes.shape({
        code: PropTypes.string,
    }),
};

const HoldingsListInPopover = ({
    holdings,
    activeHoldingId,
    onSelect,
    itemRefs,
    onClearAllFilters,
    activeTab,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const hasScrolledRef = useRef(false);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const filteredHoldings = holdings.filter((h) =>
        h.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        if (
            !hasScrolledRef.current &&
            activeHoldingId &&
            itemRefs.current[activeHoldingId]
        ) {
            itemRefs.current[activeHoldingId].scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
            hasScrolledRef.current = true;
        }
    }, [activeHoldingId, itemRefs]);

    return (
        <div className={s.CompanyListWrapper}>
            <div
                className={classNames(s.SearchInputContainer, {
                    [s['SearchInputContainer--Active']]: searchTerm.length > 0,
                })}>
                <Icon name="Search" className={s.SearchIcon} size="Small" />
                <input
                    type="text"
                    placeholder={t('general.searchKeywords')}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className={s.SearchInput}
                />
            </div>
            <ul className={s.CompanyList}>
                {filteredHoldings.map((holding) => {
                    const isActive = holding.sourceId === activeHoldingId;
                    const isDisabled = holding.hasNetwork === false;
                    return (
                        <li
                            key={holding.sourceId}
                            ref={(el) =>
                                (itemRefs.current[holding.sourceId] = el)
                            }
                            className={classNames(s.CompanyItem, {
                                [s['CompanyItem--Active']]: isActive,
                                [s['CompanyItem--Disabled']]:
                                    isDisabled && activeTab !== 'company',
                            })}
                            onClick={() => onSelect(holding)}>
                            <div className={s.CompanyItemContent}>
                                <div className={s.TextWrapper}>
                                    <span className={s.CompanyName}>
                                        {holding.name || holding.value}
                                    </span>
                                </div>
                            </div>
                        </li>
                    );
                })}
                {filteredHoldings.length === 0 && (
                    <li className={s.NoResults}>
                        <p>{t('general.noResults')}</p>
                    </li>
                )}
            </ul>
            {activeHoldingId && (
                <div className={s.FilterButton}>
                    <button
                        className={s.ClearAllFilters}
                        onClick={onClearAllFilters}>
                        {t('mapFilter.clearSelection')}
                        <Icon name="Close" className={s.FilterIcon} />
                    </button>
                </div>
            )}
        </div>
    );
};

HoldingsListInPopover.propTypes = {
    holdings: PropTypes.array.isRequired,
    activeHoldingId: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    itemRefs: PropTypes.object.isRequired,
    onClearAllFilters: PropTypes.func.isRequired,
};

export default CompanySelect;
